/*******************************************************************************
  DownloadRounds.js

  Author: Mark Duckworth; Advait; 

*******************************************************************************/

import React, { useState, useEffect } from "react";
import { API } from "aws-amplify";
import { downloadFile } from "../apihelpers/apihelpers";
import "../index.css";
import VisualTour from "./VisualTour";

/**
 * Component to let a user select an inspection / security patrol round from a list of rounds,
 * that occurred in the past month. After selecting the round, the user can
 * click a link to download details about that round.
 *
 */
function DownloadRoundData({ roundType, siteid, sitename, tenantName, sysAdmin }) {
  const [rounds, setRounds] = useState([]);
  const [selectedRound, setSelectedRound] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [openVisualTour, setopenVisualTour] = useState(false);

  // Determine round name based on type
  function getRoundName() {
    switch (roundType) {
      case "inspections":
        return "Inspection";
      case "patrols":
        return "Security Patrol";
      default:
        return "Round";
    }
  }

  function getRoundTypeForFilename() {
    switch (roundType) {
      case "inspections":
        return "inspection";
      case "patrols":
        return "patrol";
      default:
        return "unknown_round";
    }
  }

  useEffect(() => {
    if (!siteid) return;

    async function fetchRounds() {
      const now = new Date();
      let endTime = new Date();
      endTime.setDate(now.getDate() + 2); // end in future is ok, to make sure the date comes out ahead of
      // the current date in the site's timezone
      let startTime = new Date(endTime);
      startTime.setDate(endTime.getDate() - 32); // make sure we get at least a full month
      startTime = startTime.toISOString().substring(0, 11) + "00:00:00";
      endTime = endTime.toISOString().substring(0, 11) + "00:00:00";
      try {
        const response = await API.get(
          "fataskman",
          `/sites/${siteid}/${roundType}?start=${startTime}&end=${endTime}`
        );
        // List of rounds to choose from.
        // Sort to put newest at top of list.
        setRounds(response.sort((a, b) => b.startTime.localeCompare(a.startTime)));
      } catch (err) {
        console.error("Error fetching rounds:", err);
      }
    }
    fetchRounds();
  }, [siteid, roundType]);

  // reset selected round when site is changed
  useEffect(() => {
    setSelectedRound(null);
  }, [siteid]);

  function handleSelectChange(e) {
    const selectedSK = e.target.value;
    const selectedOption = e.target.options[e.target.selectedIndex];
    setSelectedRound(selectedSK);
    setSelectedTime(selectedOption.text);
  }

  function handleDownload(e) {
    e.preventDefault();
    if (!selectedRound) return;

    // convert time format from yyyy-mm-dd hh:mm:ss to yyyymmdd_hhmmss
    const timestamp = selectedTime.replace(/-/g, "").replace(" ", "_").replace(/:/g, "");
    const extension = "csv";
    const filename = sysAdmin
      ? `${getRoundTypeForFilename()}_${tenantName}_${sitename}_${timestamp}.${extension}`
      : `${getRoundTypeForFilename()}_${sitename}_${timestamp}.${extension}`;

    downloadFile(`/sites/${siteid}/${roundType}/${selectedRound}`, filename);
  }

  function startVisualTour(e) {
    e.preventDefault();
    if (!selectedRound) return;
    console.log("View Visual Tour is clicked");
    setopenVisualTour(true);
  }
  return (
    <div className="individualContainer">
      <table className="individualContainerTable">
        <tbody>
          <tr>
            <td className="individualContainerTitleColumn" valign="top">
              <h4 className="individualContainerTitleHeading">
                {roundType === "inspections"
                  ? `Select an ${getRoundName()} Round`
                  : `Select a ${getRoundName()} Round`}
              </h4>
            </td>
            <td>
              <table>
                <tbody>
                  <tr>
                    <td>
                      <select value={selectedRound || ""} onChange={handleSelectChange}>
                        {roundType === "inspections" ? (
                          <option value="">
                            -- Please Choose an {getRoundName()} Round --
                          </option>
                        ) : (
                          <option value="">
                            -- Please Choose a {getRoundName()} Round --
                          </option>
                        )}
                        {rounds.map((round) => (
                          <option key={round.SK} value={round.SK}>
                            {round.startTime}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className="button-container" style={{ paddingTop: "10px" }}>
                        <button
                          className="saveButton download-round-buttons"
                          onClick={handleDownload}
                          disabled={selectedRound ? false : true}
                        >
                          {`Download ${getRoundName()} Data`}
                        </button>
                        {roundType === "inspections" && (
                          <button
                            className="saveButton download-round-buttons"
                            onClick={startVisualTour}
                            disabled={selectedRound ? false : true}
                          >
                            View Visual Tour
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {openVisualTour && (
        <VisualTour
          siteid={siteid}
          sitename={sitename}
          selectedRound={selectedRound}
          onClose={() => setopenVisualTour(false)}
        />
      )}
    </div>
  );
}
export default DownloadRoundData;
